import React, { useRef, useState } from 'react';
import { FiUser } from 'react-icons/fi';
import { toast } from '../../../Toast';
import { WidgetDef, GENERATE_NOTE_SINGLE } from '../types';

const TABS = ['My profile', 'My holdings', 'My activity', 'My questions'];

const ACTIVITY = [
    { title: 'Quarterly Activities & Appendix 5B', meta: '31 Jul 2026 · announcement', what: 'Downloaded' },
    { title: 'Investor Presentation — August 2026', meta: '14 Aug 2026 · presentation', what: 'Viewed' },
    { title: 'Site visit recap — Gold Mountain', meta: '2 Sep 2026 · news article', what: 'Viewed' },
];

const QUESTIONS = [
    { title: 'Will there be another SPP this year?', meta: 'Asked 2 Sep 2026', answered: false },
    { title: 'When is the AGM?', meta: 'Asked 12 Aug 2026 · answered 13 Aug 2026', answered: true },
];

const preview = (what: string) => toast.info(`Preview only: ${what}`);

// spec 698:29741 — the spec stacks all four tabs under labels; a tab click scrolls to its section
const myAccount: WidgetDef = {
    slug: 'my-account',
    name: 'My account',
    description: 'Profile, holdings, activity and questions',
    icon: <FiUser />,
    title: 'My Account Widget',
    installed: false,
    page: null,
    expectedPage: '/investors/my-account/',
    views: null,
    metaStatus: 'Not on the website yet',
    demoPath: '/investors/my-account/',
    addNote: 'The account page for signed-in investors — profile, holdings, activity and questions. Not signed in? It shows the sign-in card instead.',
    addBlocks: [
        { title: '1 · WordPress shortcode', code: '[investi_my_account instance="my-account"]' },
        { title: '2 · Or plain HTML', code: '<div data-investi-instance="my-account" data-investi="my-account"></div>', dark: true },
        { title: '3 · One tab on its own', text: 'Add ?tab=holdings, ?tab=activity or ?tab=questions to open the page on that tab, or use data-investi-tab to lock it to one.' },
    ],
    settings: [
        { kind: 'heading', label: 'What they can change' },
        { kind: 'toggle', key: 'nameEmail', label: 'Name and email', default: true },
        { kind: 'toggle', key: 'password', label: 'Password', default: true },
        { kind: 'toggle', key: 'emailAlerts', label: 'Email alerts', default: true },
        { kind: 'toggle', key: 'closeAccount', label: 'Close their account', hint: 'Removes them from Contacts', default: false },
    ],
    generateNote: GENERATE_NOTE_SINGLE,
    Demo: ({ settings: s }) => {
        const [tab, setTab] = useState(0);
        const sections = useRef<(HTMLElement | null)[]>([]);
        const show = (i: number) => {
            setTab(i);
            sections.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        };
        const locked = !s.nameEmail;
        return <div className="wd-my-account">
            <div className="head">
                <span className="wd-avatar">WT</span>
                <div className="t"><h3>My account</h3><span>Wallace Turner · wallace@example.com</span></div>
                <button type="button" className="pill-btn" onClick={() => preview('nobody was signed out')}>Sign Out</button>
            </div>
            <div className="tabs" role="tablist">
                {TABS.map((t, i) => <button key={t} type="button" role="tab" aria-selected={i === tab} className={i === tab ? 'active' : ''} onClick={() => show(i)}>{t}</button>)}
            </div>

            <section className="panel" ref={el => { sections.current[0] = el; }}>
                <h4>My profile</h4>
                <div className="fields">
                    <label><span>First name</span><input defaultValue="Wallace" disabled={locked} /></label>
                    <label><span>Last name</span><input defaultValue="Turner" disabled={locked} /></label>
                    <label><span>Email address</span><input defaultValue="wallace@example.com" disabled={locked} /></label>
                    {s.password && <label><span>Password</span><input defaultValue="Change password" readOnly onClick={() => preview('no password was changed')} /></label>}
                </div>
                {s.emailAlerts && <div className="alerts">
                    <div className="t"><strong>Email alerts</strong><small>ASX announcements · Media releases and other news</small></div>
                    <button type="button" className="soft-btn" onClick={() => preview('the alerts were not changed')}>Change</button>
                </div>}
                <div className="save">
                    <button type="button" className="dark-btn" onClick={() => preview('the changes were not saved')}>Save Changes</button>
                    {s.closeAccount && <button type="button" className="close" onClick={() => preview('the account was not closed')}>Close my account</button>}
                </div>
            </section>

            <div className="wd-label">My holdings</div>
            <section className="panel" ref={el => { sections.current[1] = el; }}>
                <h4>My holdings</h4>
                <p>Shown only when their email matches the share register</p>
                <div className="stats">
                    <div><small>Shares held</small><strong>1,250,000</strong></div>
                    <div><small>% of issued capital</small><strong>0.42%</strong></div>
                    <div><small>Holder number</small><strong>X****4821</strong></div>
                </div>
                <div className="line"><div className="t"><span>Last movement</span><small>Purchase · 14 Aug 2026</small></div><strong>+50,000</strong></div>
                <div className="line"><div className="t"><span>Registry</span><small>Update your address or bank details with the registry</small></div><strong>Automic Group</strong></div>
            </section>

            <div className="wd-label">My activity</div>
            <section className="panel" ref={el => { sections.current[2] = el; }}>
                <h4>My activity</h4>
                <p>Everything they have opened on the website</p>
                {ACTIVITY.map(a => <div key={a.title} className="line"><div className="t"><span>{a.title}</span><small>{a.meta}</small></div><strong>{a.what}</strong></div>)}
            </section>

            <div className="wd-label">My questions</div>
            <section className="panel" ref={el => { sections.current[3] = el; }}>
                <h4>My questions</h4>
                <p>Questions they have asked, and the published answers</p>
                {QUESTIONS.map(q => <div key={q.title} className="line">
                    <div className="t"><span>{q.title}</span><small>{q.meta}</small></div>
                    <span className={`status ${q.answered ? 'answered' : 'awaiting'}`}>{q.answered ? 'Answered' : 'Awaiting'}</span>
                </div>)}
            </section>
        </div>;
    },
};

export default myAccount;
